import { useState, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import Papa from 'papaparse'
import { useCollection, useFirestoreMutations } from '../../hooks/useFirestore'
import { RELATIONSHIP_TYPES, DIRECTIONS } from '../../lib/relationshipTypes'
import { syncReciprocals } from '../../lib/relationshipSync'
import './RelationshipImport.css'

export default function RelationshipImport({ isOpen, onClose }) {
  const { data: characters } = useCollection('characters', { orderByField: 'name' })
  const { update } = useFirestoreMutations('characters')

  const [parsedData, setParsedData] = useState([])
  const [errors, setErrors] = useState([])
  const [importing, setImporting] = useState(false)
  const [importResult, setImportResult] = useState(null)

  // Build lookup maps
  const characterByName = characters.reduce((acc, char) => {
    acc[char.name.toLowerCase()] = char
    return acc
  }, {})

  const validTypes = RELATIONSHIP_TYPES.map(t => t.id)
  const validDirections = DIRECTIONS.map(d => d.id)

  const downloadTemplate = () => {
    const headers = ['source_name', 'target_name', 'type', 'direction', 'notes', 'hidden']
    const exampleRows = [
      ['Character A', 'Character B', 'companion', 'mutual', 'Traveling partners', ''],
      ['Character A', 'Character C', 'mentor', 'from', 'Trained under them', ''],
      ['Character B', 'Character D', 'rival', 'to', '', 'true'],
    ]

    const csvContent = [
      headers.join(','),
      ...exampleRows.map(row => row.map(cell =>
        cell.includes(',') ? `"${cell}"` : cell
      ).join(','))
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'relationships_template.csv'
    link.click()
    URL.revokeObjectURL(url)
  }

  const validateRow = (row, rowIndex) => {
    const issues = []

    // Required fields
    if (!row.source_name?.trim()) {
      issues.push('Missing source_name')
    }
    if (!row.target_name?.trim()) {
      issues.push('Missing target_name')
    }
    if (!row.type?.trim()) {
      issues.push('Missing type')
    }
    if (!row.direction?.trim()) {
      issues.push('Missing direction')
    }

    // Validate source exists
    const sourceName = row.source_name?.trim().toLowerCase()
    const sourceChar = characterByName[sourceName]
    if (row.source_name && !sourceChar) {
      issues.push(`Unknown source: "${row.source_name}"`)
    }

    // Validate target exists
    const targetName = row.target_name?.trim().toLowerCase()
    const targetChar = characterByName[targetName]
    if (row.target_name && !targetChar) {
      issues.push(`Unknown target: "${row.target_name}"`)
    }

    // Validate type
    const type = row.type?.trim().toLowerCase()
    if (type && !validTypes.includes(type)) {
      issues.push(`Invalid type: "${row.type}" (valid: ${validTypes.join(', ')})`)
    }

    // Validate direction
    const direction = row.direction?.trim().toLowerCase()
    if (direction && !validDirections.includes(direction)) {
      issues.push(`Invalid direction: "${row.direction}" (valid: ${validDirections.join(', ')})`)
    }

    // Parse hidden flag (accepts "true", "1", "yes", "hidden")
    const hiddenStr = row.hidden?.trim().toLowerCase()
    const hidden = ['true', '1', 'yes', 'hidden'].includes(hiddenStr)

    return {
      row: rowIndex + 1,
      data: row,
      sourceChar,
      targetChar,
      type,
      direction,
      notes: row.notes?.trim() || '',
      hidden,
      valid: issues.length === 0,
      issues
    }
  }

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0]
    if (!file) return

    setImportResult(null)

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const validated = results.data.map((row, i) => validateRow(row, i))
        setParsedData(validated)

        const errs = validated.filter(v => !v.valid)
        setErrors(errs)
      },
      error: (err) => {
        setErrors([{ row: 0, issues: [`Parse error: ${err.message}`] }])
      }
    })
  }, [characterByName])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'text/plain': ['.csv', '.txt']
    },
    multiple: false
  })

  const handleImport = async () => {
    const validRows = parsedData.filter(r => r.valid)
    if (validRows.length === 0) return

    setImporting(true)

    try {
      // Group by source character
      const relationshipsBySource = {}

      for (const row of validRows) {
        const sourceId = row.sourceChar.id
        if (!relationshipsBySource[sourceId]) {
          relationshipsBySource[sourceId] = {
            char: row.sourceChar,
            newRelationships: []
          }
        }

        relationshipsBySource[sourceId].newRelationships.push({
          targetId: row.targetChar.id,
          type: row.type,
          direction: row.direction,
          notes: row.notes,
          hidden: row.hidden
        })
      }

      // Update each source character
      let updated = 0
      let added = 0

      for (const sourceId of Object.keys(relationshipsBySource)) {
        const { char, newRelationships } = relationshipsBySource[sourceId]
        const existingRels = char.relationships || []

        // Merge, avoiding duplicates
        const merged = [...existingRels]

        for (const newRel of newRelationships) {
          const isDuplicate = merged.some(
            r => r.targetId === newRel.targetId &&
                 r.type === newRel.type &&
                 r.direction === newRel.direction
          )
          if (!isDuplicate) {
            merged.push(newRel)
            added++
          }
        }

        if (merged.length > existingRels.length) {
          await update(sourceId, { relationships: merged })
          updated++

          // Sync reciprocal relationships to target characters
          await syncReciprocals(sourceId, existingRels, merged)
        }
      }

      setImportResult({
        success: true,
        message: `Imported ${added} relationships across ${updated} characters.`
      })

      // Clear parsed data after success
      setParsedData([])
      setErrors([])
    } catch (err) {
      setImportResult({
        success: false,
        message: `Import failed: ${err.message}`
      })
    }

    setImporting(false)
  }

  const handleClose = () => {
    setParsedData([])
    setErrors([])
    setImportResult(null)
    onClose()
  }

  if (!isOpen) return null

  const validCount = parsedData.filter(r => r.valid).length
  const invalidCount = parsedData.filter(r => !r.valid).length

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content import-modal" onClick={e => e.stopPropagation()}>
        <h2>Import Relationships from CSV</h2>

        <div className="import-instructions">
          <p>CSV format: <code>source_name,target_name,type,direction,notes,hidden</code></p>
          <p className="help-text">
            Types: {validTypes.join(', ')} <br />
            Directions: {validDirections.join(', ')}
          </p>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={downloadTemplate}
          >
            Download Template
          </button>
        </div>

        {/* Dropzone */}
        <div
          {...getRootProps()}
          className={`dropzone ${isDragActive ? 'active' : ''}`}
        >
          <input {...getInputProps()} />
          {isDragActive ? (
            <p>Drop CSV file here...</p>
          ) : (
            <p>Drag & drop a CSV file, or click to select</p>
          )}
        </div>

        {/* Results */}
        {importResult && (
          <div className={`import-result ${importResult.success ? 'success' : 'error'}`}>
            {importResult.message}
          </div>
        )}

        {/* Preview */}
        {parsedData.length > 0 && (
          <div className="import-preview">
            <h3>
              Preview: {validCount} valid, {invalidCount} with errors
            </h3>

            <div className="preview-table-wrapper">
              <table className="preview-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Source</th>
                    <th>Target</th>
                    <th>Type</th>
                    <th>Dir</th>
                    <th>Notes</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {parsedData.slice(0, 50).map((row, i) => (
                    <tr key={i} className={row.valid ? '' : 'invalid'}>
                      <td>{row.row}</td>
                      <td>{row.data.source_name}</td>
                      <td>{row.data.target_name}</td>
                      <td>{row.data.type}</td>
                      <td>{row.data.direction}</td>
                      <td className="notes-cell">{row.data.notes || '-'}</td>
                      <td>
                        {row.valid ? (
                          <span className="status-ok">OK</span>
                        ) : (
                          <span className="status-error" title={row.issues.join('\n')}>
                            Error
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {parsedData.length > 50 && (
              <p className="help-text">Showing first 50 of {parsedData.length} rows</p>
            )}

            {/* Error details */}
            {errors.length > 0 && (
              <div className="import-errors">
                <h4>Errors ({errors.length})</h4>
                <ul>
                  {errors.slice(0, 10).map((e, i) => (
                    <li key={i}>
                      Row {e.row}: {e.issues.join('; ')}
                    </li>
                  ))}
                  {errors.length > 10 && (
                    <li>...and {errors.length - 10} more errors</li>
                  )}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="modal-actions">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={handleClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleImport}
            disabled={importing || validCount === 0}
          >
            {importing ? 'Importing...' : `Import ${validCount} Relationships`}
          </button>
        </div>
      </div>
    </div>
  )
}
